import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Layers2,
  Pencil,
  Plus,
  Trash2,
  Bell,
  BellOff,
  Settings2,
  Users,
  Check,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  MOCK_PROJECTS,
  capacityLevel,
  type TeamMember,
  type MockProject,
  type ProjectAllocation,
} from "./mockData";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getProjectColor(projects: MockProject[], projectId: string): string {
  return projects.find((p) => p.id === projectId)?.color ?? "#94a3b8";
}

function totalAlloc(allocs: ProjectAllocation[]): number {
  return allocs.reduce((s, a) => s + a.percent, 0);
}

const PROJECT_COLORS = [
  "#6366f1", "#ec4899", "#f59e0b", "#10b981", "#3b82f6",
  "#f97316", "#14b8a6", "#8b5cf6", "#ef4444", "#94a3b8",
  "#84cc16", "#06b6d4",
];

// ── Stacked allocation bar ────────────────────────────────────────────────────

interface AllocBarProps {
  allocs: ProjectAllocation[];
  total: number;
  avatarColor: string;
  projects: MockProject[];
}

function AllocBar({ allocs, total, avatarColor, projects }: AllocBarProps) {
  const display = Math.max(total, 100);

  if (allocs.length === 0) {
    return (
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.min(total, 100)}%`, backgroundColor: avatarColor }}
        />
      </div>
    );
  }

  return (
    <div className="h-2 rounded-full bg-muted overflow-hidden flex">
      {allocs.map((a) => (
        <div
          key={a.projectId}
          className="h-full transition-all duration-500"
          style={{
            width: `${(a.percent / display) * 100}%`,
            backgroundColor: getProjectColor(projects, a.projectId),
          }}
          title={`${a.projectName}: ${a.percent}%`}
        />
      ))}
    </div>
  );
}

// ── Add Project Row (in allocation dialog) ────────────────────────────────────

function AddProjectRow({
  availableProjects,
  onAdd,
  label,
}: {
  availableProjects: { id: string; name: string }[];
  onAdd: (id: string) => void;
  label: string;
}) {
  const [selected, setSelected] = useState("");

  function handleAdd() {
    if (selected) {
      onAdd(selected);
      setSelected("");
    }
  }

  return (
    <div className="flex items-center gap-2">
      <select
        className="flex-1 text-xs rounded-lg border border-border bg-background px-2 py-1.5"
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
      >
        <option value="">{label} …</option>
        {availableProjects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={handleAdd}
        disabled={!selected}
        className={cn(
          "h-7 w-7 flex items-center justify-center rounded-lg border transition-colors shrink-0",
          selected
            ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20"
            : "border-border bg-muted text-muted-foreground cursor-not-allowed opacity-50",
        )}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ── Manage Projects Dialog ────────────────────────────────────────────────────

interface ManageProjectsDialogProps {
  open: boolean;
  onClose: () => void;
  projects: MockProject[];
  onSave: (projects: MockProject[]) => void;
}

function ManageProjectsDialog({
  open,
  onClose,
  projects,
  onSave,
}: ManageProjectsDialogProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<MockProject[]>([]);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(PROJECT_COLORS[0]);

  useEffect(() => {
    if (open) {
      setDraft([...projects]);
      setNewName("");
      setNewColor(PROJECT_COLORS[0]);
    }
  }, [open, projects]);

  function renameProject(id: string, name: string) {
    setDraft((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)));
  }

  function recolorProject(id: string, color: string) {
    setDraft((prev) => prev.map((p) => (p.id === id ? { ...p, color } : p)));
  }

  function deleteProject(id: string) {
    setDraft((prev) => prev.filter((p) => p.id !== id));
  }

  function addProject() {
    const name = newName.trim();
    if (!name) return;
    const id = `proj_${Date.now()}`;
    setDraft((prev) => [...prev, { id, name, color: newColor }]);
    setNewName("");
    setNewColor(PROJECT_COLORS[0]);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-violet-500/10">
              <Settings2 className="h-4 w-4 text-violet-400" />
            </div>
            <DialogTitle className="text-sm font-semibold">
              {t("teamOverview.manageProjects", "Manage Projects")}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-3">
          {/* Existing projects */}
          <div className="space-y-2">
            {draft.map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                {/* Color picker */}
                <div className="relative shrink-0 group">
                  <div
                    className="h-5 w-5 rounded-full cursor-pointer ring-2 ring-background border border-border/40"
                    style={{ backgroundColor: p.color }}
                  />
                  <div className="absolute left-0 top-7 z-10 hidden group-hover:grid grid-cols-4 gap-1 p-2 rounded-lg border border-border bg-popover shadow-md w-24">
                    {PROJECT_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => recolorProject(p.id, c)}
                        className="h-4 w-4 rounded-full border-2 transition-all"
                        style={{
                          backgroundColor: c,
                          borderColor: p.color === c ? "white" : "transparent",
                        }}
                      />
                    ))}
                  </div>
                </div>
                <input
                  type="text"
                  value={p.name}
                  onChange={(e) => renameProject(p.id, e.target.value)}
                  className="flex-1 text-xs rounded-lg border border-border bg-background px-2 py-1.5 min-w-0"
                />
                <button
                  type="button"
                  onClick={() => deleteProject(p.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>

          {/* Add new project */}
          <div className="pt-2 border-t border-border/40">
            <p className="text-xs font-medium text-muted-foreground mb-2">
              {t("teamOverview.addNewProject", "Add new project")}
            </p>
            <div className="flex items-center gap-2">
              {/* Color swatches for new */}
              <div className="flex gap-1 shrink-0">
                {PROJECT_COLORS.slice(0, 5).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewColor(c)}
                    className="h-4 w-4 rounded-full border-2 transition-all shrink-0"
                    style={{
                      backgroundColor: c,
                      borderColor: newColor === c ? "white" : "transparent",
                      outline: newColor === c ? `2px solid ${c}` : "none",
                      outlineOffset: "1px",
                    }}
                  />
                ))}
              </div>
              <input
                type="text"
                placeholder={t("teamOverview.projectName", "Project name…")}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addProject()}
                className="flex-1 text-xs rounded-lg border border-border bg-background px-2 py-1.5 min-w-0"
              />
              <button
                type="button"
                onClick={addProject}
                disabled={!newName.trim()}
                className={cn(
                  "h-7 w-7 flex items-center justify-center rounded-lg border transition-colors shrink-0",
                  newName.trim()
                    ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20"
                    : "border-border bg-muted text-muted-foreground cursor-not-allowed opacity-50",
                )}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            {/* More colors row */}
            <div className="flex gap-1 mt-2">
              {PROJECT_COLORS.slice(5).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewColor(c)}
                  className="h-4 w-4 rounded-full border-2 transition-all shrink-0"
                  style={{
                    backgroundColor: c,
                    borderColor: newColor === c ? "white" : "transparent",
                    outline: newColor === c ? `2px solid ${c}` : "none",
                    outlineOffset: "1px",
                  }}
                />
              ))}
            </div>
          </div>

          {/* Save/cancel */}
          <div className="flex gap-2 justify-end pt-2 border-t border-border/40">
            <Button size="sm" variant="ghost" className="text-xs" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              size="sm"
              className="text-xs gradient-brand text-primary-foreground gap-1.5"
              onClick={() => { onSave(draft); onClose(); }}
            >
              <Check className="h-3 w-3" />
              {t("common.save")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Allocation Edit Dialog ────────────────────────────────────────────────────

interface AllocationEditDialogProps {
  open: boolean;
  onClose: () => void;
  members: TeamMember[];
  projects: MockProject[];
  isHR: boolean;
  isLineManager: boolean;
  allocationState: Record<number, ProjectAllocation[]>;
  onSave: (memberId: number, allocs: ProjectAllocation[]) => Promise<void> | void;
}

function AllocationEditDialog({
  open,
  onClose,
  members,
  projects,
  isHR,
  isLineManager,
  allocationState,
  onSave,
}: AllocationEditDialogProps) {
  const { t } = useTranslation();

  const editableMembers = useMemo(
    () => (isHR || isLineManager ? members : []),
    [members, isHR, isLineManager],
  );

  const [selectedId, setSelectedId] = useState<number>(
    editableMembers[0]?.id ?? 0,
  );
  const [draft, setDraft] = useState<ProjectAllocation[]>([]);
  const [notify, setNotify] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const editableMembersRef = useRef(editableMembers);
  editableMembersRef.current = editableMembers;
  const allocationStateRef = useRef(allocationState);
  allocationStateRef.current = allocationState;

  useEffect(() => {
    if (open && editableMembersRef.current.length > 0) {
      const firstId = editableMembersRef.current[0].id;
      setSelectedId(firstId);
      setDraft([
        ...(allocationStateRef.current[firstId] ??
          editableMembersRef.current[0].allocations ??
          []),
      ]);
    }
  }, [open]);

  const selectedMember = editableMembers.find((m) => m.id === selectedId);
  const savedAllocs =
    allocationState[selectedId] ?? selectedMember?.allocations ?? [];

  function openForMember(id: number) {
    setSelectedId(id);
    setDraft([
      ...(allocationState[id] ??
        members.find((m) => m.id === id)?.allocations ??
        []),
    ]);
  }

  const usedProjectIds = draft.map((a) => a.projectId);
  const availableProjects = projects.filter(
    (p) => !usedProjectIds.includes(p.id),
  );
  const total = totalAlloc(draft);

  function updatePercent(projectId: string, value: number) {
    setDraft((prev) =>
      prev.map((a) =>
        a.projectId === projectId
          ? { ...a, percent: Math.max(0, Math.min(200, value)) }
          : a,
      ),
    );
  }

  function removeProject(projectId: string) {
    setDraft((prev) => prev.filter((a) => a.projectId !== projectId));
  }

  function addProject(projectId: string) {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    setDraft((prev) => [
      ...prev,
      { projectId: project.id, projectName: project.name, percent: 0 },
    ]);
  }

  async function handleSave() {
    setIsSaving(true);
    try {
      await onSave(selectedId, draft);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  }

  if (!selectedMember) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-sky-500/10">
              <Layers2 className="h-4 w-4 text-sky-400" />
            </div>
            <DialogTitle className="text-sm font-semibold">
              {t("teamOverview.adjustAllocation")}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {isHR && (
            <div className="flex items-center gap-2 rounded-xl border border-border/60 px-3 py-2 bg-muted/30">
              <button
                className={cn(
                  "flex items-center gap-1.5 text-xs flex-1",
                  notify ? "text-foreground font-medium" : "text-muted-foreground",
                )}
                onClick={() => setNotify(true)}
              >
                <Bell className={cn("h-3 w-3", notify && "text-indigo-400")} />
                {t("teamOverview.sendNotification")}
              </button>
              <div className="w-px h-4 bg-border/60" />
              <button
                className={cn(
                  "flex items-center gap-1.5 text-xs flex-1",
                  !notify ? "text-foreground font-medium" : "text-muted-foreground",
                )}
                onClick={() => setNotify(false)}
              >
                <BellOff className={cn("h-3 w-3", !notify && "text-amber-400")} />
                {t("teamOverview.updateSilently")}
              </button>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              {t("teamOverview.employee")}
            </label>
            <select
              className="w-full text-sm rounded-lg border border-border bg-background px-3 py-2"
              value={selectedId}
              onChange={(e) => openForMember(Number(e.target.value))}
            >
              {editableMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">
                {t("teamOverview.allocationFor", {
                  name: selectedMember.name.split(" ")[0],
                })}
              </label>
              <span
                className={cn(
                  "text-xs font-semibold tabular-nums",
                  total > 100
                    ? "text-rose-400"
                    : total === 100
                      ? "text-emerald-400"
                      : "text-amber-400",
                )}
              >
                {t("teamOverview.totalAllocation")}: {total}%
              </span>
            </div>

            {draft.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-3">
                {t("teamOverview.noProjectsAdded")}
              </p>
            )}

            {draft.map((a) => (
              <div key={a.projectId} className="flex items-center gap-2">
                <div
                  className="h-3 w-3 rounded-sm shrink-0"
                  style={{ backgroundColor: getProjectColor(projects, a.projectId) }}
                />
                <span className="text-xs flex-1 truncate">{a.projectName}</span>
                <input
                  type="number"
                  min={0}
                  max={200}
                  value={a.percent}
                  onChange={(e) =>
                    updatePercent(a.projectId, Number(e.target.value))
                  }
                  className="w-16 text-xs rounded-lg border border-border bg-background px-2 py-1 text-right tabular-nums"
                />
                <span className="text-xs text-muted-foreground">%</span>
                <button
                  onClick={() => removeProject(a.projectId)}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>

          {availableProjects.length > 0 && (
            <AddProjectRow
              availableProjects={availableProjects}
              onAdd={addProject}
              label={t("teamOverview.addProject")}
            />
          )}

          <div className="flex gap-2 justify-end pt-2 border-t border-border/40">
            <Button size="sm" variant="ghost" className="text-xs" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              size="sm"
              className="text-xs gradient-brand text-primary-foreground"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? t("common.saving", "Saving...") : t("common.save")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Widget ───────────────────────────────────────────────────────────────

interface CapacityAllocationWidgetProps {
  members: TeamMember[];
  isHR?: boolean;
  isLineManager?: boolean;
  onSaveAlloc?: (memberId: number, allocs: ProjectAllocation[]) => Promise<void>;
}

export function CapacityAllocationWidget({
  members,
  isHR = false,
  isLineManager = false,
  onSaveAlloc,
}: CapacityAllocationWidgetProps) {
  const { t } = useTranslation();

  const [showEdit, setShowEdit] = useState(false);
  const [showManageProjects, setShowManageProjects] = useState(false);
  const [activeTab, setActiveTab] = useState<"people" | "projects">("people");
  const [projects, setProjects] = useState<MockProject[]>([...MOCK_PROJECTS]);
  const [allocationState, setAllocationState] = useState<
    Record<number, ProjectAllocation[]>
  >({});

  const effectiveAllocs = (m: TeamMember): ProjectAllocation[] =>
    allocationState[m.id] ?? m.allocations;

  const sorted = useMemo(
    () => [...members].sort((a, b) => b.capacityPercent - a.capacityPercent),
    [members],
  );

  const avgCapacity = Math.round(
    members.reduce((s, m) => s + m.capacityPercent, 0) / (members.length || 1),
  );

  const overCount = members.filter((m) => m.capacityPercent > 100).length;
  const busyCount = members.filter(
    (m) => m.capacityPercent > 80 && m.capacityPercent <= 100,
  ).length;

  const canEdit = isHR || isLineManager;

  // Per-project aggregated stats
  const projectStats = useMemo(() =>
    projects
      .map((p) => {
        const assignedMembers = sorted.filter((m) =>
          effectiveAllocs(m).some((a) => a.projectId === p.id),
        );
        const totalPercent = sorted.reduce((sum, m) => {
          const alloc = effectiveAllocs(m).find((a) => a.projectId === p.id);
          return sum + (alloc?.percent ?? 0);
        }, 0);
        return { ...p, assignedMembers, totalPercent };
      })
      .filter((p) => p.totalPercent > 0 || p.assignedMembers.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, sorted, allocationState],
  );

  async function handleSaveAlloc(memberId: number, allocs: ProjectAllocation[]) {
    if (onSaveAlloc) {
      await onSaveAlloc(memberId, allocs);
    }
    setAllocationState((prev) => ({ ...prev, [memberId]: allocs }));
  }

  return (
    <>
      <Card className="flex flex-col h-full">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-sky-500/10">
                <Layers2 className="h-4 w-4 text-sky-400" />
              </div>
              <div>
                <CardTitle className="text-sm font-semibold">
                  {t("teamOverview.capacityByProject")}
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("teamOverview.capacityByProjectSub")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              {overCount > 0 && (
                <Badge
                  variant="outline"
                  className="text-[10px] border-rose-500/40 text-rose-400 bg-rose-500/10"
                >
                  {overCount} {t("teamOverview.overallocated")}
                </Badge>
              )}
              {busyCount > 0 && (
                <Badge
                  variant="outline"
                  className="text-[10px] border-amber-500/40 text-amber-400 bg-amber-500/10"
                >
                  {busyCount} {t("teamOverview.busy")}
                </Badge>
              )}
              {canEdit && (
                <button
                  onClick={() => setShowManageProjects(true)}
                  className="h-6 w-6 flex items-center justify-center rounded-lg border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  title={t("teamOverview.manageProjects", "Manage Projects")}
                >
                  <Settings2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col gap-3">
          {/* Avg ring + legend */}
          <div className="flex items-center gap-4 pb-2 border-b border-border/40">
            <div className="relative h-14 w-14 shrink-0">
              <svg viewBox="0 0 36 36" className="h-14 w-14 -rotate-90">
                <circle
                  cx="18"
                  cy="18"
                  r="15.9"
                  fill="none"
                  stroke="currentColor"
                  className="text-muted/40"
                  strokeWidth="3"
                />
                <circle
                  cx="18"
                  cy="18"
                  r="15.9"
                  fill="none"
                  stroke={
                    avgCapacity > 100
                      ? "#f43f5e"
                      : avgCapacity > 80
                        ? "#f59e0b"
                        : "#10b981"
                  }
                  strokeWidth="3"
                  strokeDasharray={`${Math.min(avgCapacity, 120)} 120`}
                  strokeLinecap="round"
                  className="transition-all duration-700"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold">
                {avgCapacity}%
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">
                {t("teamOverview.avgUtilization")}
              </p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
                {projects.map((p) => (
                  <span
                    key={p.id}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground"
                  >
                    <span
                      className="h-2 w-2 rounded-sm shrink-0"
                      style={{ backgroundColor: p.color }}
                    />
                    {p.name}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex rounded-lg border border-border/60 overflow-hidden text-xs font-medium">
            <button
              onClick={() => setActiveTab("people")}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-1.5 transition-colors",
                activeTab === "people"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted/40",
              )}
            >
              <Users className="h-3 w-3" />
              {t("teamOverview.byPeople", "By People")}
            </button>
            <div className="w-px bg-border/60" />
            <button
              onClick={() => setActiveTab("projects")}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-1.5 transition-colors",
                activeTab === "projects"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted/40",
              )}
            >
              <Layers2 className="h-3 w-3" />
              {t("teamOverview.byProject", "By Project")}
            </button>
          </div>

          {/* ── By People tab ─── */}
          {activeTab === "people" && (
            <div className="space-y-2.5">
              {sorted.map((m) => {
                const allocs = effectiveAllocs(m);
                const tot = totalAlloc(allocs);
                const capLvl = capacityLevel(m.capacityPercent);
                const capColor =
                  capLvl === "over"
                    ? "text-rose-400"
                    : capLvl === "busy"
                      ? "text-amber-400"
                      : "text-emerald-400";

                return (
                  <div key={m.id}>
                    <div className="flex items-center gap-2 mb-1">
                      <div
                        className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-semibold text-white shrink-0"
                        style={{ background: m.avatarColor }}
                      >
                        {m.initials}
                      </div>
                      <span className="text-xs font-medium flex-1 truncate">
                        {m.name.split(" ")[0]}
                      </span>
                      <span className={cn("text-[11px] font-semibold tabular-nums", capColor)}>
                        {tot}%
                      </span>
                    </div>
                    <AllocBar
                      allocs={allocs}
                      total={tot}
                      avatarColor={m.avatarColor}
                      projects={projects}
                    />
                    {allocs.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {allocs.map((a) => {
                          const c = getProjectColor(projects, a.projectId);
                          return (
                            <span
                              key={a.projectId}
                              className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full border"
                              style={{
                                color: c,
                                borderColor: `${c}40`,
                                backgroundColor: `${c}12`,
                              }}
                            >
                              <span
                                className="h-1.5 w-1.5 rounded-full shrink-0"
                                style={{ backgroundColor: c }}
                              />
                              {a.projectName} · {a.percent}%
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── By Project tab ─── */}
          {activeTab === "projects" && (
            <div className="space-y-3">
              {projectStats.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {t("teamOverview.noAllocationsYet", "No allocations set yet.")}
                </p>
              ) : (
                projectStats.map((p) => {
                  // avg utilisation per head for this project
                  const avgPct =
                    p.assignedMembers.length > 0
                      ? Math.round(p.totalPercent / p.assignedMembers.length)
                      : 0;
                  return (
                    <div
                      key={p.id}
                      className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2.5 space-y-2"
                    >
                      {/* Project header */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="h-3 w-3 rounded-sm shrink-0"
                            style={{ backgroundColor: p.color }}
                          />
                          <span className="text-xs font-semibold truncate">
                            {p.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[10px] text-muted-foreground">
                            {p.assignedMembers.length}{" "}
                            {p.assignedMembers.length === 1
                              ? t("teamOverview.person", "person")
                              : t("teamOverview.people", "people")}
                          </span>
                          <span
                            className="text-xs font-bold tabular-nums"
                            style={{ color: p.color }}
                          >
                            {p.totalPercent}%
                          </span>
                        </div>
                      </div>

                      {/* Bar: total % / (100 * headcount) */}
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min(avgPct, 100)}%`,
                            backgroundColor: p.color,
                          }}
                        />
                      </div>

                      {/* Member avatars */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {p.assignedMembers.map((m) => {
                          const alloc = effectiveAllocs(m).find(
                            (a) => a.projectId === p.id,
                          );
                          return (
                            <div
                              key={m.id}
                              className="flex items-center gap-1"
                              title={`${m.name}: ${alloc?.percent ?? 0}%`}
                            >
                              <div
                                className="h-5 w-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white shrink-0 ring-1 ring-background"
                                style={{ backgroundColor: m.avatarColor }}
                              >
                                {m.initials}
                              </div>
                              <span className="text-[9px] text-muted-foreground tabular-nums">
                                {alloc?.percent ?? 0}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              className="w-full text-xs gap-1.5 mt-auto"
              onClick={() => setShowEdit(true)}
            >
              <Pencil className="h-3 w-3" />
              {t("teamOverview.adjustAllocation")}
            </Button>
          )}
        </CardContent>
      </Card>

      <AllocationEditDialog
        open={showEdit}
        onClose={() => setShowEdit(false)}
        members={members}
        projects={projects}
        isHR={isHR}
        isLineManager={isLineManager}
        allocationState={allocationState}
        onSave={handleSaveAlloc}
      />

      <ManageProjectsDialog
        open={showManageProjects}
        onClose={() => setShowManageProjects(false)}
        projects={projects}
        onSave={(updated) => setProjects(updated)}
      />
    </>
  );
}
